import { useEffect } from "react";

/**
 * Runtime visual guard for site navbars.
 *
 * Verifies, on every route, that:
 *   1. the navbar background luminance is dark enough
 *   2. the contrast ratio between text and background meets WCAG AA
 *      (>= 4.5:1 for normal text)
 *
 * Logs structured warnings (with route, computed colors, ratio) when a
 * navbar fails. Active in dev only.
 */

type RGB = [number, number, number];

function parseColor(c: string): RGB | null {
  const m = c.match(/rgba?\(([^)]+)\)/);
  if (!m) return null;
  const parts = m[1].split(",").map((v) => parseFloat(v.trim()));
  if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) return null;
  return [parts[0], parts[1], parts[2]];
}

function srgbToLinear(c: number) {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

function relativeLuminance([r, g, b]: RGB) {
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}

function contrastRatio(a: RGB, b: RGB) {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Walk up DOM until we hit a non-transparent background. */
function effectiveBackground(el: HTMLElement): RGB | null {
  let node: HTMLElement | null = el;
  while (node) {
    const bg = getComputedStyle(node).backgroundColor;
    const rgb = parseColor(bg);
    const m = bg.match(/rgba?\(([^)]+)\)/);
    const alpha = m ? parseFloat(m[1].split(",")[3] ?? "1") : 1;
    if (rgb && alpha > 0.5) return rgb;
    node = node.parentElement;
  }
  return null;
}

function checkNavbar(nav: HTMLElement, route: string) {
  const bg = effectiveBackground(nav);
  if (!bg) return;
  const lum = relativeLuminance(bg);

  // 1. Background must be dark.
  if (lum > 0.25) {
    // eslint-disable-next-line no-console
    console.warn("[NavbarDarkGuard] Sfondo navbar troppo chiaro", {
      route,
      backgroundRgb: bg,
      luminance: lum.toFixed(3),
    });
  }

  // 2. Sample interactive text contrast.
  const samples = nav.querySelectorAll<HTMLElement>("a, button, [role='button']");
  let worst: { ratio: number; el: HTMLElement; fg: RGB } | null = null;
  samples.forEach((el) => {
    if (el.offsetParent === null) return; // hidden
    const fg = parseColor(getComputedStyle(el).color);
    if (!fg) return;
    const ratio = contrastRatio(fg, bg);
    if (!worst || ratio < (worst as { ratio: number }).ratio) worst = { ratio, el, fg };
  });

  const w = worst as { ratio: number; el: HTMLElement; fg: RGB } | null;
  if (w && w.ratio < 4.5) {
    // eslint-disable-next-line no-console
    console.warn("[NavbarDarkGuard] Contrasto testo navbar < WCAG AA (4.5:1)", {
      route,
      ratio: w.ratio.toFixed(2),
      foregroundRgb: w.fg,
      backgroundRgb: bg,
      element: w.el.outerHTML.slice(0, 200),
    });
  }
}

export function NavbarDarkGuard() {
  useEffect(() => {
    if (!import.meta.env.DEV) return;

    const run = () => {
      const route = window.location.pathname;
      const navs = document.querySelectorAll<HTMLElement>(
        '[data-testid="site-navbar"], [data-testid="site-navbar-panel"]'
      );
      navs.forEach((nav) => checkNavbar(nav, route));
    };

    run();
    const id = window.setTimeout(run, 600);
    window.addEventListener("popstate", run);

    // Also re-check when DOM mutates significantly (e.g. drawer opens).
    const mo = new MutationObserver(() => {
      window.clearTimeout((mo as any)._t);
      (mo as any)._t = window.setTimeout(run, 200);
    });
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["class", "data-theme"] });

    return () => {
      window.clearTimeout(id);
      window.removeEventListener("popstate", run);
      mo.disconnect();
    };
  }, []);
  return null;
}
